import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  Info,
  HardDrive,
  Cpu,
  Activity,
  Server,
  Check,
  Eye,
  Loader2,
  Ticket
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Alerts = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, critical: 0, warning: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [acknowledging, setAcknowledging] = useState({});
  const [resolving, setResolving] = useState({});

  useEffect(() => {
    fetchData();
    // Refresh every minute
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [statusFilter, severityFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (severityFilter !== 'all') params.severity = severityFilter;
      
      const [alertsRes, summaryRes] = await Promise.all([
        api.get('/alerts', { params }),
        api.get('/alerts/summary')
      ]);
      
      setAlerts(alertsRes.data.alerts || []);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    setAcknowledging({ ...acknowledging, [alertId]: true });
    try {
      await api.put(`/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    } finally {
      setAcknowledging({ ...acknowledging, [alertId]: false });
    }
  };

  const handleResolve = async (alertId) => {
    setResolving({ ...resolving, [alertId]: true });
    try {
      await api.put(`/alerts/${alertId}/resolve`);
      toast.success('Alert resolved');
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve alert');
    } finally {
      setResolving({ ...resolving, [alertId]: false });
    }
  };

  const [creatingTicket, setCreatingTicket] = useState({});

  const handleCreateTicket = async (alertId) => {
    setCreatingTicket({ ...creatingTicket, [alertId]: true });
    try {
      const response = await api.post(`/alerts/${alertId}/create-ticket`);
      toast.success('Ticket created from alert!');
      fetchData();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setCreatingTicket({ ...creatingTicket, [alertId]: false });
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      case 'warning': return <Clock className="w-4 h-4" />;
      case 'info': return <Info className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getAlertTypeIcon = (type) => {
    switch (type) {
      case 'disk_space': return <HardDrive className="w-5 h-5" />;
      case 'cpu_usage': return <Cpu className="w-5 h-5" />;
      case 'ram_usage': return <Activity className="w-5 h-5" />;
      case 'service_stopped': return <Server className="w-5 h-5" />;
      default: return <AlertTriangle className="w-5 h-5" />;
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">System Alerts</h1>
        <p className="text-slate-600 mt-1">Monitor and manage asset alerts</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-xs md:text-sm text-slate-600">Total Alerts</p>
              <p className="text-xl md:text-3xl font-bold text-slate-900">{summary.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('active')}>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-xs md:text-sm text-blue-600">Active</p>
              <p className="text-xl md:text-3xl font-bold text-blue-700">{summary.active}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('active'); setSeverityFilter('critical'); }}>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-xs md:text-sm text-red-600">Critical</p>
              <p className="text-xl md:text-3xl font-bold text-red-700">{summary.critical}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('active'); setSeverityFilter('warning'); }}>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-xs md:text-sm text-yellow-600">Warning</p>
              <p className="text-xl md:text-3xl font-bold text-yellow-700">{summary.warning}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Filters Row */}
            <div className="flex flex-col sm:flex-row gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} className="sm:ml-auto">
              Refresh
            </Button>
          </div>

          {/* Legend */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-2">Action Buttons:</p>
            <div className="flex flex-wrap gap-4 text-xs text-slate-600">
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>Acknowledge - Mark as seen</span>
              </div>
              <div className="flex items-center gap-1">
                <Ticket className="w-4 h-4 text-blue-600" />
                <span>Create Ticket - Convert to support ticket</span>
              </div>
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4" />
                <span>Resolve - Close the alert</span>
              </div>
            </div>
            <p className="text-sm font-semibold text-slate-700 mb-2 mt-3">Alert Types:</p>
            <div className="flex flex-wrap gap-4 text-xs text-slate-600">
              <div className="flex items-center gap-1">
                <HardDrive className="w-4 h-4" />
                <span>Disk Space</span>
              </div>
              <div className="flex items-center gap-1">
                <Cpu className="w-4 h-4" />
                <span>CPU Usage</span>
              </div>
              <div className="flex items-center gap-1">
                <Activity className="w-4 h-4" />
                <span>RAM Usage</span>
              </div>
              <div className="flex items-center gap-1">
                <Server className="w-4 h-4" />
                <span>Service Stopped</span>
              </div>
            </div>
          </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No alerts found</h3>
            <p className="text-slate-500">All systems are running normally</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <Card key={alert.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {/* Alert Icon */}
                  <div className={`p-3 rounded-lg flex-shrink-0 ${
                    alert.severity === 'critical' ? 'bg-red-50' :
                    alert.severity === 'warning' ? 'bg-yellow-50' :
                    'bg-blue-50'
                  }`}>
                    {getAlertTypeIcon(alert.alert_type)}
                  </div>

                  {/* Alert Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-semibold text-slate-800 break-words">{alert.rule_name}</h3>
                      <Badge className={getSeverityColor(alert.severity)}>
                        <div className="flex items-center gap-1">
                          {getSeverityIcon(alert.severity)}
                          <span>{alert.severity}</span>
                        </div>
                      </Badge>
                      {alert.acknowledged && (
                        <Badge variant="outline" className="text-green-600">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Acknowledged
                        </Badge>
                      )}
                      {alert.status === 'resolved' && (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          Resolved
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span 
                        className="text-sm text-blue-600 hover:underline cursor-pointer"
                        onClick={() => navigate(`/assets/${alert.agent_id}`)}
                      >
                        {alert.agent_name}
                      </span>
                      <span className="text-slate-400">•</span>
                      <span className="text-sm text-slate-500">
                        {new Date(alert.created_at).toLocaleString()}
                      </span>
                    </div>

                    {/* Alert Details */}
                    <div className="bg-slate-50 rounded-lg p-3 mt-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        {Object.entries(alert.details).map(([key, value]) => (
                          <div key={key}>
                            <span className="text-slate-600">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>{' '}
                            <span className="font-medium text-slate-800">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {alert.status === 'active' && (
                    <TooltipProvider>
                      <div className="flex lg:flex-col gap-2 flex-shrink-0">
                        {!alert.acknowledged && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAcknowledge(alert.id)}
                                disabled={acknowledging[alert.id]}
                                className="w-full lg:w-auto"
                              >
                                {acknowledging[alert.id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Eye className="w-4 h-4 mr-1" />
                                    <span className="lg:hidden">Acknowledge</span>
                                  </>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Mark alert as seen/acknowledged</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {!alert.ticket_id && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCreateTicket(alert.id)}
                                disabled={creatingTicket[alert.id]}
                                className="w-full lg:w-auto border-blue-300 text-blue-700 hover:bg-blue-50"
                              >
                                {creatingTicket[alert.id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Ticket className="w-4 h-4 mr-1" />
                                    <span className="lg:hidden">Create Ticket</span>
                                  </>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Create a support ticket from this alert</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              onClick={() => handleResolve(alert.id)}
                              disabled={resolving[alert.id]}
                              className="w-full lg:w-auto"
                            >
                              {resolving[alert.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-1" />
                                  <span className="lg:hidden">Resolve</span>
                                </>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Mark alert as resolved and close it</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Alerts;
